<template>
  <BaseTemplate app-name="Gestão de Preparos" page-title="">
    <template #content>
      <!-- Verificação de permissão -->
      <div v-if="!temPermissao" class="container mx-auto p-6">
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div class="mb-4">
            <svg class="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-4">Acesso Negado</h2>
          <p class="text-gray-600 dark:text-gray-300 mb-6">
            Você não tem permissão para acessar esta página. Apenas usuários com permissões de administrador podem gerenciar preparos.
          </p>
          <button @click="voltar" class="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg">
            Voltar
          </button>
        </div>
      </div>

      <div v-else class="container mx-auto p-6">
        <!-- Botão Voltar -->
        <button @click="voltar"
          class="hidden md:inline-flex mb-5 items-center gap-2 bg-blue-500 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:hover:bg-blue-700">
          <SquareArrowLeft class="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <!-- Cabeçalho com títulos e botões -->
        <div class="flex flex-col sm:flex-row justify-between items-center mb-6">
          <h1 class="flex items-center gap-2 text-2xl font-bold text-gray-900 dark:text-white">
            <FileText class="w-6 h-6 text-blue-500" />
            Gestão de Preparos
          </h1>
          <div class="flex flex-wrap items-center gap-3 mt-4 sm:mt-0">
            <!-- Botão "Novo Preparo" -->
            <button @click="openModal()"
              class="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg shadow">
              <PlusIcon class="w-5 h-5" />
              Novo Preparo
            </button>
          </div>
        </div>

        <!-- Filtro rápido (busca por título) -->
        <div class="flex flex-col sm:flex-row gap-4 mb-6">
          <input v-model="filter" type="text" placeholder="Buscar por título..."
            class="w-full sm:w-1/2 border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white focus:ring-2 focus:ring-blue-500" />
          <div class="flex items-center gap-2">
            <label class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input v-model="showInactive" type="checkbox" class="rounded" />
              Mostrar inativos
            </label>
          </div>
        </div>

        <!-- Mensagem de erro -->
        <p v-if="error" class="text-red-500 text-center mb-4">{{ error }}</p>

        <!-- Tabela desktop -->
        <div
          class="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto dark:text-white text-gray-800">
          <table class="w-full">
            <thead
              class="bg-blue-500 dark:bg-blue-600 text-white dark:text-gray-300 text-sm uppercase tracking-wide rounded-t-lg">
              <tr>
                <th @click="ordenarPor('titulo')" class="cursor-pointer px-4 py-3 text-left">
                  Título <span v-if="colunaOrdenada === 'titulo'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-left">Conteúdo (prévia)</th>
                <th @click="ordenarPor('ativo')" class="cursor-pointer px-4 py-3 text-left">
                  Status <span v-if="colunaOrdenada === 'ativo'">{{ ordemAscendente ? '🠕' : '🠗' }}</span>
                </th>
                <th class="px-4 py-3 text-left">Feito por</th>
                <th class="px-4 py-3 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="preparo in preparosOrdenados" :key="preparo.id"
                class="border-b dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                <td class="px-4 py-3 font-medium">{{ preparo.titulo }}</td>
                <td class="px-4 py-3">{{ getTextoPrevia(preparo.conteudo) }}</td>
                <td class="px-4 py-3">
                  <span :class="preparo.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                    class="px-2 py-1 rounded-full text-xs font-semibold">
                    {{ preparo.ativo ? 'Ativo' : 'Inativo' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm">
                  <div class="space-y-1">
                    <div v-if="preparo.criado_por" class="text-gray-600 dark:text-gray-400">
                      <span class="font-semibold">Criado:</span> {{ preparo.criado_por }}
                    </div>
                    <div v-if="preparo.atualizado_por" class="text-gray-600 dark:text-gray-400">
                      <span class="font-semibold"></span> {{ preparo.atualizado_por }}
                    </div>
                    <div v-if="preparo.desativado_por" class="text-red-600 dark:text-red-400">
                      <span class="font-semibold">Desativado:</span> {{ preparo.desativado_por }}
                    </div>
                  </div>
                </td>
                <td class="px-4 py-3">
                  <div class="flex justify-center gap-2">
                    <button @click="openPreview(preparo)"
                      class="p-2 text-green-600 hover:bg-green-100 dark:hover:bg-green-900 rounded"
                      title="Visualizar como ficará no portal">
                      <EyeIcon class="w-4 h-4" />
                    </button>
                    <button @click="editPreparo(preparo)"
                      class="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900 rounded">
                      <Edit2Icon class="w-4 h-4" />
                    </button>
                    <button @click="deletePreparo(preparo)"
                      class="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900 rounded">
                      <Trash2Icon class="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cards mobile -->
        <div class="sm:hidden space-y-4">
          <div v-for="preparo in preparosOrdenados" :key="preparo.id"
            class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="flex justify-between items-start mb-2">
              <h3 class="font-semibold text-gray-900 dark:text-white">{{ preparo.titulo }}</h3>
              <span :class="preparo.ativo ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'"
                class="px-2 py-1 rounded-full text-xs font-semibold">
                {{ preparo.ativo ? 'Ativo' : 'Inativo' }}
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-400 mb-3">{{ getTextoPrevia(preparo.conteudo, 150) }}</p>
            <div class="text-xs text-gray-500 dark:text-gray-400 mb-3 space-y-1">
              <div v-if="preparo.criado_por">
                <span class="font-semibold">Criado por:</span> {{ preparo.criado_por }}
              </div>
              <div v-if="preparo.atualizado_por">
                <span class="font-semibold">Atualizado por:</span> {{ preparo.atualizado_por }}
              </div>
              <div v-if="preparo.desativado_por" class="text-red-600 dark:text-red-400">
                <span class="font-semibold">Desativado por:</span> {{ preparo.desativado_por }}
              </div>
            </div>
            <div class="flex gap-2">
              <button @click="openPreview(preparo)"
                class="flex-1 flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded">
                <EyeIcon class="w-4 h-4" />
                Visualizar
              </button>
              <button @click="editPreparo(preparo)"
                class="flex-1 flex items-center justify-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded">
                <Edit2Icon class="w-4 h-4" />
                Editar
              </button>
              <button @click="deletePreparo(preparo)"
                class="flex-1 flex items-center justify-center gap-1 bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded">
                <Trash2Icon class="w-4 h-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>

        <!-- Mensagem quando não há preparos -->
        <div v-if="preparosFiltrados.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
          <p>Nenhum preparo encontrado.</p>
        </div>

        <!-- Modal de criação/edição -->
        <div v-if="isModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 pt-16 pb-4"  @click.self="closeModal">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl animate-fade-in max-h-[90vh] flex flex-col my-auto">
            <div class="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                <FileText class="w-5 h-5 text-blue-500" />
                {{ isEditMode ? "Editar Preparo" : "Novo Preparo" }}
              </h2>
            </div>
            
            <!-- Mensagem de erro dentro do modal -->
            <div v-if="error" class="px-6 pt-4">
              <div class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p class="text-red-600 dark:text-red-400 text-sm font-medium">{{ error }}</p>
              </div>
            </div>
            
            <div class="flex-1 overflow-y-auto p-6">
              <!-- Campo Título -->
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Título <span class="text-red-500">*</span></label>
              <input v-model="currentPreparo.titulo" type="text" required
                class="w-full border rounded-lg px-4 py-2 dark:bg-gray-700 dark:border-gray-600 dark:text-white mb-3"
                placeholder="Ex: Preparo para Exame de Sangue..." />

              <!-- Campo Conteúdo -->
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conteúdo <span class="text-red-500">*</span></label>
              <div class="border border-gray-300 dark:border-gray-600 rounded-lg mb-3">
                <QuillEditor
                  v-model:content="currentPreparo.conteudo"
                  contentType="html"
                  theme="snow"
                  :modules="editorModules"
                  :toolbar="editorToolbarOptions"
                  placeholder="Digite o conteúdo do preparo..."
                />
              </div>

              <!-- Campo Ativo -->
              <label class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                <input v-model="currentPreparo.ativo" type="checkbox" class="rounded" />
                Preparo ativo
              </label>

              <!-- Campo Fixo -->
              <label class="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <input @change="handleFixoChange" v-model="currentPreparo.fixo" type="checkbox" class="rounded" />
                Fixo no topo (exibir centralizado acima dos outros preparos)
              </label>
            </div>

            <!-- Botões Salvar / Cancelar - Fixos no rodapé -->
            <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-white dark:bg-gray-800">
              <button @click="closeModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button @click="savePreparo"
                class="flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg">
                <SaveIcon class="w-4 h-4" />
                <span>Salvar</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Prévia -->
        <div v-if="isPreviewModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 pt-16 pb-4"
          @click.self="closePreviewModal">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-4xl animate-fade-in max-h-[90vh] flex flex-col my-auto">
            <div class="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                <EyeIcon class="w-5 h-5 text-green-500" />
                Prévia - Como ficará no Portal
              </h2>
            </div>

            <div class="flex-1 overflow-y-auto p-6">
              <!-- Card de prévia simulando o portal -->
              <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden transition-all hover:shadow-md">
                <!-- Cabeçalho do card (simulando o portal) -->
                <div class="w-full text-left p-6 flex justify-between items-center bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <h3 class="font-semibold text-lg text-[#005baa] dark:text-blue-400 pr-4">{{ preparoPreview?.titulo }}</h3>
                  <ChevronDown class="h-5 w-5 text-[#005baa] dark:text-blue-400 transition-transform duration-200 flex-shrink-0 transform rotate-180" />
                </div>

                <!-- Conteúdo expandido (simulando o portal) -->
                <div class="px-6 pb-6 bg-white dark:bg-gray-800">
                  <div class="pt-4 border-t border-gray-200 dark:border-gray-600">
                    <div class="dark:text-gray-100 prose-content">
                      <QuillContent v-if="preparoPreview?.conteudo" :conteudo="preparoPreview.conteudo" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Botões Fechar - Fixos no rodapé -->
            <div class="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3 bg-white dark:bg-gray-800">
              <button @click="closePreviewModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Fechar
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Confirmação de Exclusão -->
        <div v-if="isDeleteModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 py-4"
          @click.self="closeDeleteModal">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md animate-fade-in my-auto">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <Trash2Icon class="w-5 h-5 text-red-500" />
              Confirmar Exclusão
            </h2>
            
            <p class="text-gray-700 dark:text-gray-300 mb-6">
              Tem certeza que deseja excluir o preparo <strong>"{{ preparoToDelete?.titulo }}"</strong>?
            </p>

            <div class="flex justify-end space-x-3">
              <button @click="closeDeleteModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button @click="confirmDelete"
                class="flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg">
                <Trash2Icon class="w-4 h-4" />
                Excluir
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de Confirmação de Fixo -->
        <div v-if="isFixoConfirmModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 py-4"
          @click.self="closeFixoConfirmModal">
          <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-md animate-fade-in my-auto">
            <h2 class="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Confirmar Preparo Fixo
            </h2>
            
            <p class="text-gray-700 dark:text-gray-300 mb-4">
              Já existe um preparo fixo no topo: <strong>"{{ preparoFixoAtual?.titulo }}"</strong>
            </p>
            <p class="text-gray-700 dark:text-gray-300 mb-6">
              Deseja remover o fixo atual e marcar <strong>"{{ preparoParaFixo?.titulo }}"</strong> como fixo?
            </p>

            <div class="flex justify-end space-x-3">
              <button @click="closeFixoConfirmModal"
                class="flex items-center gap-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg">
                <XIcon class="w-4 h-4" /> Cancelar
              </button>
              <button @click="confirmFixo"
                class="flex items-center gap-1 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg">
                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Confirmar
              </button>
            </div>
          </div>
        </div>
      </div>

    </template>
  </BaseTemplate>
</template>

<script setup>
import apiClient from '@/services/authService';
import BaseTemplate from '../templates/BaseTemplate.vue';
import { ref, computed, onMounted, watch } from 'vue';
import {
  FileText,
  PlusIcon,
  Edit2Icon,
  Trash2Icon,
  XIcon,
  SaveIcon,
  SquareArrowLeft,
  EyeIcon,
  ChevronDown
} from 'lucide-vue-next';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';
import ImageResize from 'quill-image-resize-vue';
import QuillContent from '@/views/templates/QuillContent.vue';

const router = useRouter();
const userStore = useUserStore();
const userGroups = computed(() => userStore.grupos || []);

// Verificar permissões
const gruposPermitidos = ['superadmin', 'desenvolvedor', 'preparos'];
const temPermissao = computed(() => {
  return gruposPermitidos.some(grupo => userGroups.value.includes(grupo));
});

const voltar = () => router.go(-1);

const preparos = ref([]);
const filter = ref('');
const error = ref(null);
const isModalOpen = ref(false);
const isEditMode = ref(false);
const showInactive = ref(false);
const isPreviewModalOpen = ref(false);
const preparoPreview = ref(null);
const isDeleteModalOpen = ref(false);
const preparoToDelete = ref(null);
const isFixoConfirmModalOpen = ref(false);
const preparoFixoAtual = ref(null);
const preparoParaFixo = ref(null);

const currentPreparo = ref({
  id: null,
  titulo: '',
  conteudo: '',
  ativo: true,
  fixo: false
});

// Configurações para o editor Quill
const editorModules = {
  name: 'imageResize',
  module: ImageResize,
  options: {
    displaySize: true,
  },
};

const editorToolbarOptions = [
  ['bold', 'italic', 'underline', 'strike'],
  ['blockquote', 'code-block'],
  [{ header: 1 }, { header: 2 }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ direction: 'rtl' }],
  [{ size: ['small', false, 'large', 'huge'] }],
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  [{ color: [] }, { background: [] }],
  [{ font: [] }],
  [{ align: [] }],
  ['clean'],
  ['link', 'image', 'video'],
];

// Ordenação
const colunaOrdenada = ref('titulo');
const ordemAscendente = ref(true);

// Filtragem reativa de preparos
const preparosFiltrados = computed(() =>
  preparos.value.filter(preparo => {
    const tituloMatch = preparo.titulo.toLowerCase().includes(filter.value.toLowerCase());
    const ativoMatch = showInactive.value || preparo.ativo;
    return tituloMatch && ativoMatch;
  })
);

// Computed para ordenar
const preparosOrdenados = computed(() => {
  const arr = preparosFiltrados.value.slice();
  arr.sort((a, b) => {
    let aVal = a[colunaOrdenada.value];
    let bVal = b[colunaOrdenada.value];
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (aVal < bVal) return ordemAscendente.value ? -1 : 1;
    if (aVal > bVal) return ordemAscendente.value ? 1 : -1;
    return 0;
  });
  return arr;
});

function ordenarPor(coluna) {
  if (colunaOrdenada.value === coluna) {
    ordemAscendente.value = !ordemAscendente.value;
  } else {
    colunaOrdenada.value = coluna;
    ordemAscendente.value = true;
  }
}

async function loadPreparos() {
  try {
    error.value = null;
    const endpoint = showInactive.value ? '/api/v1/preparo/todos/' : '/api/v1/preparo/';
    const response = await apiClient.get(endpoint);
    preparos.value = response.data;
  } catch (err) {
    error.value = 'Erro ao carregar preparos.';
    console.error('Erro ao carregar preparos:', err);
  }
}

function openModal(preparo = null) {
  error.value = null; // Limpar erro ao abrir o modal
  if (preparo) {
    isEditMode.value = true;
    currentPreparo.value = {
      id: preparo.id,
      titulo: preparo.titulo,
      conteudo: preparo.conteudo || '',
      ativo: preparo.ativo,
      fixo: preparo.fixo || false
    };
  } else {
    isEditMode.value = false;
    currentPreparo.value = {
      id: null,
      titulo: '',
      conteudo: '',
      ativo: true,
      fixo: false
    };
  }
  isModalOpen.value = true;
}

function closeModal() {
  isModalOpen.value = false;
  error.value = null; // Limpar erro ao fechar o modal
  currentPreparo.value = {
    id: null,
    titulo: '',
    conteudo: '',
    ativo: true,
    fixo: false
  };
}

async function savePreparo() {
  if (!currentPreparo.value.titulo.trim()) {
    error.value = 'O título do preparo é obrigatório.';
    return;
  }
  // Remover tags HTML vazias e verificar se há conteúdo real
  const conteudoLimpo = currentPreparo.value.conteudo 
    ? currentPreparo.value.conteudo.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() 
    : '';
  if (!conteudoLimpo || conteudoLimpo === '') {
    error.value = 'O conteúdo do preparo é obrigatório.';
    return;
  }

  try {
    error.value = null;
    if (isEditMode.value) {
      await apiClient.put(`/api/v1/preparo/${currentPreparo.value.id}/`, {
        titulo: currentPreparo.value.titulo,
        conteudo: currentPreparo.value.conteudo,
        ativo: currentPreparo.value.ativo,
        fixo: currentPreparo.value.fixo
      });
    } else {
      await apiClient.post('/api/v1/preparo/', {
        titulo: currentPreparo.value.titulo,
        conteudo: currentPreparo.value.conteudo,
        ativo: currentPreparo.value.ativo,
        fixo: currentPreparo.value.fixo
      });
    }
    await loadPreparos();
    closeModal();
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Erro ao salvar preparo.';
    error.value = errorMessage;
    console.error('Erro ao salvar preparo:', err);
  }
}

function editPreparo(preparo) {
  openModal(preparo);
}

// Função para remover HTML e mostrar apenas texto na prévia
function getTextoPrevia(conteudo, maxLength = 100) {
  if (!conteudo) return '-';
  
  // Remove tags HTML e entidades
  let texto = conteudo
    .replace(/<[^>]*>/g, '') // Remove tags HTML
    .replace(/&nbsp;/g, ' ') // Substitui &nbsp; por espaço
    .replace(/&amp;/g, '&') // Substitui &amp; por &
    .replace(/&lt;/g, '<') // Substitui &lt; por <
    .replace(/&gt;/g, '>') // Substitui &gt; por >
    .replace(/&quot;/g, '"') // Substitui &quot; por "
    .replace(/&#39;/g, "'") // Substitui &#39; por '
    .trim();
  
  if (texto.length === 0) return '-';
  
  if (texto.length > maxLength) {
    return texto.substring(0, maxLength) + '...';
  }
  
  return texto;
}

// Função para abrir modal de prévia
function openPreview(preparo) {
  preparoPreview.value = preparo;
  isPreviewModalOpen.value = true;
}

// Função para fechar modal de prévia
function closePreviewModal() {
  isPreviewModalOpen.value = false;
  preparoPreview.value = null;
}

function deletePreparo(preparo) {
  preparoToDelete.value = preparo;
  isDeleteModalOpen.value = true;
}

function closeDeleteModal() {
  isDeleteModalOpen.value = false;
  preparoToDelete.value = null;
}

async function confirmDelete() {
  if (!preparoToDelete.value) return;

  try {
    error.value = null;
    await apiClient.delete(`/api/v1/preparo/${preparoToDelete.value.id}/`);
    await loadPreparos();
    closeDeleteModal();
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Erro ao excluir preparo.';
    error.value = errorMessage;
    alert(errorMessage);
    console.error('Erro ao excluir preparo:', err);
  }
}

// Função para lidar com mudança no checkbox "fixo"
function handleFixoChange() {
  // Se está marcando como fixo
  if (currentPreparo.value.fixo) {
    // Verificar se já existe um preparo fixo (diferente do atual se estiver editando)
    const preparoFixoExistente = preparos.value.find(p => p.fixo && (!isEditMode.value || p.id !== currentPreparo.value.id));
    
    if (preparoFixoExistente) {
      // Desmarcar o checkbox temporariamente
      currentPreparo.value.fixo = false;
      
      // Abrir modal de confirmação
      preparoFixoAtual.value = preparoFixoExistente;
      preparoParaFixo.value = {
        id: currentPreparo.value.id,
        titulo: currentPreparo.value.titulo
      };
      isFixoConfirmModalOpen.value = true;
    }
  }
}

// Função para confirmar a mudança de fixo
async function confirmFixo() {
  try {
    error.value = null;
    
    // Primeiro, desmarcar o fixo atual
    if (preparoFixoAtual.value) {
      await apiClient.put(`/api/v1/preparo/${preparoFixoAtual.value.id}/`, {
        fixo: false
      });
    }
    
    // Depois, marcar o novo como fixo (o backend já vai garantir que apenas um seja fixo)
    currentPreparo.value.fixo = true;
    
    // Fechar modal de confirmação
    closeFixoConfirmModal();
    
    // Recarregar preparos para atualizar a lista
    await loadPreparos();
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.response?.data?.detail || 'Erro ao atualizar preparo fixo.';
    error.value = errorMessage;
    console.error('Erro ao atualizar preparo fixo:', err);
  }
}

// Função para fechar modal de confirmação de fixo
function closeFixoConfirmModal() {
  isFixoConfirmModalOpen.value = false;
  preparoFixoAtual.value = null;
  preparoParaFixo.value = null;
}

// Watcher para recarregar preparos quando o checkbox de inativos mudar
watch(showInactive, () => {
  if (temPermissao.value) {
    loadPreparos();
  }
});

onMounted(() => {
  // Verificar permissões antes de carregar dados
  if (!temPermissao.value) {
    return;
  }
  loadPreparos();
});
</script>
